/*
 * HomeFixer360 - Profile Settings Page
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Upload, User, Building, Phone, Mail, Save, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const ProfileSettings = () => {
  const { user, isAuthenticated, loading } = useAuth();
  
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    companyLogo: null
  });
  
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [subscription, setSubscription] = useState({ status: 'free', tier: null });
  const fileInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (user && isAuthenticated) {
      loadProfile();
      loadSubscription();
    }
  }, [user, isAuthenticated]);

  const loadProfile = async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/profile/me`, {
        params: { user_id: user.id }
      });
      
      const data = response.data;
      setProfile({
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        company: data.company || '',
        companyLogo: data.company_logo
      });
      
      if (data.company_logo) {
        setLogoPreview(`${BACKEND_URL}${data.company_logo}`);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      showMessage('error', 'Failed to load profile');
    }
  };

  const loadSubscription = async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/profile/subscription`, {
        params: { user_id: user.id }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      showMessage('error', 'User not authenticated');
      return;
    }
    
    setIsSaving(true);
    
    try {
      await axios.put(`${BACKEND_URL}/api/profile/me`, {
        first_name: profile.firstName,
        last_name: profile.lastName,
        phone: profile.phone,
        company: profile.company
      }, {
        params: { user_id: user.id }
      });
      
      showMessage('success', 'Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      showMessage('error', 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      showMessage('error', 'Only PNG and JPG files are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'File size must be less than 5MB');
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    if (!user?.id) {
      showMessage('error', 'User not authenticated');
      return;
    }

    // Check subscription
    if (!['active', 'trial'].includes(subscription.subscription_status)) {
      showMessage('error', 'Logo upload is only available for paying customers. Please upgrade your subscription.');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      formData.append('user_id', user.id);

      const response = await axios.post(
        `${BACKEND_URL}/api/profile/logo/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setProfile({ ...profile, companyLogo: response.data.logo_url });
      setLogoPreview(`${BACKEND_URL}${response.data.logo_url}`);
      setLogoFile(null);
      showMessage('success', 'Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload logo';
      showMessage('error', errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your logo?')) return;

    if (!user?.id) {
      showMessage('error', 'User not authenticated');
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/profile/logo`, {
        params: { user_id: user.id }
      });

      setProfile({ ...profile, companyLogo: null });
      setLogoPreview(null);
      setLogoFile(null);
      showMessage('success', 'Logo deleted successfully');
    } catch (error) {
      console.error('Error deleting logo:', error);
      showMessage('error', 'Failed to delete logo');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Show loading state while authentication is being checked
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Show error if user is not authenticated
  if (!isAuthenticated || !user) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg">Please log in to access profile settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Profile Settings</h1>
      <p className="text-gray-600 mb-6">Manage your account information and company branding</p>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 
          'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Company Logo Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Company Logo</h2>
            <p className="text-sm text-gray-600">Upload your company logo to personalize estimates and proposals</p>
            {!['active', 'trial'].includes(subscription.subscription_status) && (
              <p className="text-sm text-orange-600 mt-1">⚠️ Requires active subscription</p>
            )}
          </div>
        </div>

        <div className="flex items-start space-x-6">
          {/* Logo Preview */}
          <div className="flex-shrink-0">
            <div className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Company Logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <div className="text-center">
                  <Building className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No logo uploaded</p>
                </div>
              )}
            </div>
          </div>

          {/* Upload Controls */}
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleLogoSelect}
              className="hidden"
            />

            <div className="space-y-3">
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full"
                disabled={!['active', 'trial'].includes(subscription.subscription_status)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Choose Logo
              </Button>

              {logoFile && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800 mb-2">Ready to upload: {logoFile.name}</p>
                  <Button
                    onClick={handleLogoUpload}
                    disabled={isUploading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isUploading ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                </div>
              )}

              {profile.companyLogo && !logoFile && (
                <Button
                  onClick={handleLogoDelete}
                  variant="outline"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Logo
                </Button>
              )}

              <div className="text-xs text-gray-500 space-y-1">
                <p>• Accepted formats: PNG, JPG</p>
                <p>• Maximum file size: 5MB</p>
                <p>• Recommended: Square logo, 800x800px</p>
                <p>• Auto-resized to fit estimates and proposals</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Profile Information</h2>
        
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={profile.firstName}
                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                placeholder="John"
              />
            </div>

            <div>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={profile.lastName}
                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                placeholder="Doe"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={profile.email}
              disabled
              className="bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              placeholder="(555) 123-4567"
            />
          </div>

          <div>
            <Label htmlFor="company">Company Name</Label>
            <Input
              id="company"
              value={profile.company}
              onChange={(e) => setProfile({ ...profile, company: e.target.value })}
              placeholder="Your Company LLC"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={loadProfile}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      {/* Subscription Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Status</h2>
        <div className="space-y-2">
          <p className="text-sm">
            <span className="font-medium">Status:</span>{' '}
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              subscription.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
              subscription.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {subscription.subscription_status?.toUpperCase() || 'FREE'}
            </span>
          </p>
          {subscription.subscription_tier && (
            <p className="text-sm">
              <span className="font-medium">Plan:</span> {subscription.subscription_tier}
            </p>
          )}
          {subscription.subscription_status === 'free' && (
            <div className="mt-4">
              <Button
                onClick={() => window.location.href = '/presale-checkout'}
                className="bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
              >
                Upgrade to Access Logo Upload
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
